import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../utils/authFetch';
import { fetchAllAssessments } from './assessmentsSlice';

/**
 * Fetch all models from FetchModelInventory
 * Backend handles user ownership filtering (non-admin sees only their models)
 */
export const fetchAllModels = createAsyncThunk(
  'models/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/FetchModelInventory');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch models`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch models');
      }

      return data.data; // Array of models with assessment info
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Create a new model via SaveModelCard
 */
export const createModel = createAsyncThunk(
  'models/create',
  async (modelData, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/SaveModelCard', {
        method: 'POST',
        body: JSON.stringify({
          modelName: modelData.modelName,
          useCaseShortDescription: modelData.useCaseShortDescription,
          modelType: modelData.modelType,
          modelStage: modelData.modelStage,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to create model`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to create model');
      }

      return data.data; // Created model
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update an existing model via SaveModelCard
 */
export const updateModel = createAsyncThunk(
  'models/update',
  async ({ modelId, updates }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/SaveModelCard', {
        method: 'POST',
        body: JSON.stringify({
          modelId,
          modelName: updates.modelName,
          useCaseShortDescription: updates.useCaseShortDescription,
          modelType: updates.modelType,
          modelStage: updates.modelStage,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to update model`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to update model');
      }

      return data.data; // Updated model
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Delete a model
 * Uses the existing deleteModel endpoint with DELETE method and uuid query param
 */
export const deleteModel = createAsyncThunk(
  'models/delete',
  async (modelId, { rejectWithValue }) => {
    try {
      const response = await authFetch(`/.netlify/functions/deleteModel?uuid=${modelId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to delete model`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to delete model');
      }

      return modelId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  byId: {},
  allIds: [],
  loading: false,        // Only for initial fetch - triggers full-screen loading
  mutating: false,       // For create/update/delete - does NOT trigger full-screen loading
  error: null,
};

// Slice
const modelsSlice = createSlice({
  name: 'models',
  initialState,
  reducers: {
    // Synchronous reducers for local updates
    updateModelLocally: (state, action) => {
      const { modelId, updates } = action.payload;
      if (state.byId[modelId]) {
        state.byId[modelId] = {
          ...state.byId[modelId],
          ...updates,
        };
      }
    },

    clearModels: (state) => {
      state.byId = {};
      state.allIds = [];
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    // Fetch all models - only set loading=true on initial fetch (no data yet)
    builder
      .addCase(fetchAllModels.pending, (state) => {
        // Only show full-screen loading on initial fetch when there's no data
        // Subsequent refreshes should not trigger loading screen
        if (state.allIds.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchAllModels.fulfilled, (state, action) => {
        const models = action.payload;
        state.byId = {};
        state.allIds = [];

        models.forEach((model) => {
          const modelId = model.model_uuid;
          state.byId[modelId] = {
            // Core model data
            model_uuid: model.model_uuid,
            model_name: model.model_name,
            model_type: model.model_type,
            model_stage: model.model_stage,
            use_case_short_description: model.use_case_short_description,
            created_at: model.created_at,
            created_by: model.created_by,
            // User info
            avatar: model.avatar,
            name: model.name,
            // Assessment info (from LEFT JOIN in FetchModelInventory)
            assessment_uuid: model.assessment_uuid,
            assessment_status: model.assessment_status,
            current_section: model.current_section,
            current_step: model.current_step,
            position: model.position,
            template_id: model.template_id,
            mode: model.mode,
            autonomous: model.autonomous,
            updated_at: model.updated_at,
            owner_name: model.owner_name,
            owner_email: model.owner_email,
            review_status: model.review_status,
          };
          state.allIds.push(modelId);
        });

        state.loading = false;
      })
      .addCase(fetchAllModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create model - uses mutating flag (no full-screen loading)
    builder
      .addCase(createModel.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(createModel.fulfilled, (state, action) => {
        const model = action.payload;
        const modelId = model.model_uuid;

        state.byId[modelId] = {
          model_uuid: model.model_uuid,
          model_name: model.model_name,
          model_type: model.model_type,
          model_stage: model.model_stage,
          use_case_short_description: model.use_case_short_description,
          created_at: model.created_at,
          created_by: model.created_by,
          avatar: model.avatar,
          name: model.createdByName,
          // New model has no assessment yet
          assessment_uuid: null,
          assessment_status: 'Not Started',
        };

        if (!state.allIds.includes(modelId)) {
          state.allIds.push(modelId);
        }
        state.mutating = false;
      })
      .addCase(createModel.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload;
      });

    // Update model - uses mutating flag (no full-screen loading)
    builder
      .addCase(updateModel.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(updateModel.fulfilled, (state, action) => {
        const model = action.payload;
        const modelId = model.model_uuid;
        if (state.byId[modelId]) {
          state.byId[modelId] = {
            ...state.byId[modelId],
            model_name: model.model_name,
            model_type: model.model_type,
            model_stage: model.model_stage,
            use_case_short_description: model.use_case_short_description,
            updated_at: model.updated_at,
          };
        }
        state.mutating = false;
      })
      .addCase(updateModel.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload;
      });

    // Delete model - uses mutating flag (no full-screen loading)
    builder
      .addCase(deleteModel.pending, (state) => {
        state.mutating = true;
        state.error = null;
      })
      .addCase(deleteModel.fulfilled, (state, action) => {
        const modelId = action.payload;
        delete state.byId[modelId];
        state.allIds = state.allIds.filter((id) => id !== modelId);
        state.mutating = false;
      })
      .addCase(deleteModel.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload;
      });

    // Also populate models when assessments are fetched
    // This provides redundancy - models can come from either endpoint
    builder
      .addCase(fetchAllAssessments.fulfilled, (state, action) => {
        const assessments = action.payload;

        assessments.forEach((assessment) => {
          // Only add if assessment has a model reference
          if (assessment.model_uuid) {
            const modelId = assessment.model_uuid;

            // Add model if not already present, or update assessment info
            if (!state.byId[modelId]) {
              state.byId[modelId] = {
                model_uuid: assessment.model_uuid,
                model_name: assessment.model_name,
                model_type: assessment.model_type,
                model_stage: assessment.model_stage,
                use_case_short_description: assessment.use_case_short_description,
                created_at: assessment.created_at,
                created_by: assessment.created_by,
              };
              state.allIds.push(modelId);
            }

            // Always update assessment info from assessment fetch
            // NOTE: API returns 'status' not 'assessment_status' (see getAssessments.js:91)
            state.byId[modelId].assessment_uuid = assessment.assessment_uuid;
            state.byId[modelId].assessment_status = assessment.status;
          }
        });
      });
  },
});

export const {
  updateModelLocally,
  clearModels,
} = modelsSlice.actions;

// Selectors
export const selectModelsById = (state) => state.models.byId;
export const selectAllModelIds = (state) => state.models.allIds;
export const selectModelsLoading = (state) => state.models.loading;  // For initial fetch only
export const selectModelsMutating = (state) => state.models.mutating; // For create/update/delete
export const selectModelsError = (state) => state.models.error;

export const selectModelById = (state, modelId) => state.models.byId[modelId];

export const selectAllModels = (state) =>
  state.models.allIds.map((id) => state.models.byId[id]);

export const selectModelsCount = (state) => state.models.allIds.length;

// Models without assessments (for Start Assessment dialog)
export const selectModelsWithoutAssessments = (state) =>
  state.models.allIds
    .map((id) => state.models.byId[id])
    .filter((model) => !model.assessment_uuid);

// Models with assessments
export const selectModelsWithAssessments = (state) =>
  state.models.allIds
    .map((id) => state.models.byId[id])
    .filter((model) => model.assessment_uuid);

// ============================================================================
// Builder/Explorer Model Filtering Selectors
// ============================================================================

/**
 * Select models WITHOUT Builder assessments
 * Used in Start Assessment Dialog for Builder mode (can only create Builder for models without one)
 */
export const selectModelsWithoutBuilderAssessment = (state) => {
  const builderModelIds = new Set(
    Object.values(state.assessments.byId)
      .filter(a => a.assessment_type === 'builder' || !a.assessment_type)
      .map(a => a.model_uuid)
      .filter(Boolean)
  );

  return state.models.allIds
    .map((id) => state.models.byId[id])
    .filter((model) => !builderModelIds.has(model.model_uuid));
};

/**
 * Select ALL models with their Builder assessment status
 * Used in Start Assessment Dialog for Explorer mode (shows all models with Builder status indicator)
 */
export const selectModelsWithBuilderStatus = (state) => {
  // Build a map of model_uuid -> builder assessment
  const builderByModel = {};
  Object.values(state.assessments.byId).forEach(assessment => {
    // assessment_type is nested under info in the Redux state structure
    const assessmentType = assessment.info?.assessment_type || assessment.assessment_type;
    if ((assessmentType === 'builder' || !assessmentType) && assessment.model_uuid) {
      builderByModel[assessment.model_uuid] = assessment;
    }
  });

  // Count navigators per builder
  const navigatorCountByBuilder = {};
  Object.values(state.assessments.byId).forEach(assessment => {
    const assessmentType = assessment.info?.assessment_type || assessment.assessment_type;
    if (assessmentType === 'use_case' && assessment.parent_assessment_uuid) {
      navigatorCountByBuilder[assessment.parent_assessment_uuid] =
        (navigatorCountByBuilder[assessment.parent_assessment_uuid] || 0) + 1;
    }
  });

  return state.models.allIds.map((id) => {
    const model = state.models.byId[id];
    const builder = builderByModel[model.model_uuid];
    const builderUuid = builder?.info?.assessment_uuid;

    return {
      ...model,
      builderAssessment: builder ? {
        status: builder.info?.status,
        assessmentUuid: builder.info?.assessment_uuid,
        assessmentName: builder.info?.assessment_name,
        isCompleted: builder.info?.status === 'Completed',
        isInProgress: builder.info?.status === 'On-Going',
        navigatorCount: builderUuid ? (navigatorCountByBuilder[builderUuid] || 0) : 0
      } : null
    };
  });
};

/**
 * Get count of models without Builder assessments
 */
export const selectModelsWithoutBuilderCount = (state) => {
  return selectModelsWithoutBuilderAssessment(state).length;
};

export default modelsSlice.reducer;
